<!-- 键盘接收器的版本展示、在线检查与升级入口。 -->
<template>
  <div v-loading="isLoading" class="p-3 flex flex-col bg-key rounded-4xl">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-1 h-9 text-black-white text-l-medium">
        <Icon icon="Hardware-Wireframe-Receiver" class="size-6" />
        <span class="text-l-medium text-black-white">
          {{ t('setting.device.firmware.dongleTitle') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button color="black" size="sm" class="h-8" @click="handleOpenUpgradeList">
          {{ t('setting.device.firmware.releaseNotes') }}
        </Button>
        <div class="relative">
          <Button
            color="black"
            size="sm"
            class="h-8"
            :disabled="!canUpdate"
            @click="handleCheckVersion(canUpdate)"
          >
            {{
              !canUpdate
                ? $t('setting.about.update.latestVersion')
                : $t('setting.about.update.checkUpdatesButton')
            }}
          </Button>
          <div
            v-if="canUpdate"
            class="absolute top-0.5 right-0 w-2 h-2 rounded-full bg-brand-red"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-between items-center">
      <span class="text-m-medium text-text-glass-default">
        {{ t('setting.device.firmware.currentVersion', { version: receiverFirmwareVersion }) }}
      </span>
    </div>
  </div>

  <Dialog v-model:open="isLatestVersionTipOpen" content-class="w-90">
    <div class="flex flex-col items-center mb-2">
      <img width="210" height="210" :src="upgradeImg" />
      <p class="w-52.5 text-m-medium text-black-white text-center">
        {{ latestVersionText(canUpdate) }}
      </p>
    </div>
    <template #footer>
      <div class="w-full">
        <Button color="black" class="w-full h-12" @click="isLatestVersionTipOpen = false">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:open="isFirmwareUpgradeOpen"
    :title="t('setting.device.firmware.dongleTitle')"
    content-class="w-90"
  >
    <div>
      <img class="w-full object-contain" :src="deviceThumbImage" />
      <div class="mt-4">
        <p class="text-m-medium text-black-white text-center">
          {{ t('setting.device.firmware.upgradeDongleWarning') }}
        </p>
      </div>
      <div class="mt-6 w-full flex items-center gap-3">
        <Button color="text-b-w" class="flex-1 h-10" @click="isFirmwareUpgradeOpen = false">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" class="flex-1 h-10" @click="handleUpdateFirmware">
          {{ t('common.confirm') }}
        </Button>
      </div>
      <div class="mt-6 flex justify-center">
        <p class="text-m-medium text-text-glass-default">
          {{ t('setting.device.firmware.latestVersion', { version: latestFirmwareVersion }) }}
        </p>
      </div>
    </div>
  </Dialog>

  <Dialog
    v-model:open="upgradeDialog"
    description=""
    :close-on-overlay-click="false"
    content-class="w-145 p-6 gap-6"
  >
    <rocketProgress v-model="firmwareUpgradeProgress" />
    <div class="w-full">
      <div class="text-s-medium w-full text-center text-text-glass-default">
        {{ t('setting.device.firmware.upgradingText') }}
        <span class="text-brand-blue1">{{ firmwareUpgradeProgress }}</span> / 100
      </div>
      <div class="text-s-medium w-full text-center text-text-glass-default">
        {{ t('setting.device.firmware.upgradingTipText') }}
      </div>
    </div>
  </Dialog>

  <Dialog v-model:open="isUpgradeListOpen" content-class="w-90">
    <template #header>
      <div class="h-7 w-full flex justify-center items-center text-2xl-medium text-black-white">
        {{ t('setting.device.firmware.upgradeLogTitle') }}
      </div>
    </template>
    <ScrollArea class="h-82 pr-2">
      <div class="h-full flex flex-col gap-3">
        <template v-if="upgradeList.length > 0">
          <div v-for="(item, index) in upgradeList" :key="index">
            <div class="flex items-center text-m-medium">
              <span class="text-black-white">{{ item.Version }}</span>
              <span class="text-black-white">（{{ item.Date }}）</span>
            </div>
            <ul class="list-disc ml-6 mt-2">
              <li class="text-s-medium text-text-glass-default">
                {{ language === ZH_CN_LOCALE ? item.DescriptionZh : item.DescriptionEn }}
              </li>
            </ul>
          </div>
        </template>
        <div v-else class="mt-10 flex flex-col items-center justify-center">
          <img :src="emptyImg" width="200px" height="200px" />
          <span class="text-m-medium text-black-white">
            {{ t('setting.device.firmware.upgradeLogEmpty') }}
          </span>
        </div>
      </div>
    </ScrollArea>
    <template #footer>
      <div class="w-full">
        <Button color="black" class="w-full h-12" @click="isUpgradeListOpen = false">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { formatLatestFirmwareVersion } from '@/utils/firmwareVersion'
  import { storeToRefs } from 'pinia'
  import { Button, Dialog, Icon, ScrollArea } from '@rapoo/element'
  import { DeviceType } from '@rapoo/device-sdk'
  import { useFirmwareStore, useLocaleStore } from '@/stores'
  import rocketProgress from '@/components/rocketProgress/index.vue'
  import { useDeviceImage } from '@/composables/useDeviceImage'
  import { useFirmwareUpgrade } from '@/composables/useFirmwareUpgrade'
  import { ZH_CN_LOCALE } from '@/utils/locale'
  import upgradeImg from '@/assets/images/devices/upgrade.webp'
  import emptyImg from '@/assets/images/header/settings/empty.webp'

  const firmwareStore = useFirmwareStore()
  const { currentVersions, currentDeviceCanUpdateFirmware } = storeToRefs(firmwareStore)
  const { t, language } = useLocaleStore()
  const { deviceThumbImage } = useDeviceImage()

  const {
    isLoading,
    isLatestVersionTipOpen,
    isFirmwareUpgradeOpen,
    upgradeDialog,
    isUpgradeListOpen,
    upgradeList,
    latestVersionText,
    firmwareUpgradeProgress,
    handleCheckVersion,
    handleUpdateFirmware,
    handleOpenUpgradeList,
  } = useFirmwareUpgrade({
    modelField: 'dongleModel',
  })

  const receiverFirmwareVersion = computed(() => {
    return (
      currentVersions.value?.find((item) => item.deviceType === DeviceType.Receiver)?.version || '-'
    )
  })

  const canUpdate = computed<boolean | undefined>(() => {
    if (!currentDeviceCanUpdateFirmware.value) return undefined
    return currentDeviceCanUpdateFirmware.value.some(
      (item) => item.deviceType === DeviceType.Receiver,
    )
  })
  const latestFirmwareVersion = computed(() =>
    formatLatestFirmwareVersion(currentVersions.value, DeviceType.Receiver),
  )
</script>

<style scoped></style>
